import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import api from '../services/api';
import { TimeEntry, Contractor, Project } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { Snackbar, Alert, Checkbox } from '@mui/material';

const TimeEntries: React.FC = () => {
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<TimeEntry | null>(null);
  const [formData, setFormData] = useState({
    contractorId: '',
    projectId: '',
    dateWorked: new Date(),
    hoursWorked: '',
    description: '',
  });
  const [filters, setFilters] = useState({
    contractorId: '',
    projectId: '',
    status: '',
  });
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<TimeEntry | null>(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [selectedEntries, setSelectedEntries] = useState<string[]>([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'info' | 'warning' });
  const { user } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (filters.contractorId) params.contractorId = filters.contractorId;
      if (filters.projectId) params.projectId = filters.projectId;
      if (filters.status) params.status = filters.status;

      const [entriesRes, contractorsRes, projectsRes] = await Promise.all([
        api.get('/time-entries', { params }),
        api.get('/contractors'),
        api.get('/projects'),
      ]);

      setTimeEntries(entriesRes.data);
      setContractors(contractorsRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();

    // Auto-refresh every 30 seconds to sync status changes
    const interval = setInterval(() => {
      fetchData();
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchData]);

  const handleOpen = (entry?: TimeEntry) => {
    if (entry) {
      setEditing(entry);
      setFormData({
        contractorId: entry.contractorId,
        projectId: entry.projectId,
        dateWorked: new Date(entry.dateWorked),
        hoursWorked: entry.hoursWorked.toString(),
        description: entry.description,
      });
    } else {
      setEditing(null);
      // If contractor, pre-fill their ID
      const contractorId = user?.contractorId || '';
      setFormData({
        contractorId,
        projectId: '',
        dateWorked: new Date(),
        hoursWorked: '',
        description: '',
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
  };

  const handleSubmit = async () => {
    try {
      const data = {
        ...formData,
        dateWorked: formData.dateWorked.toISOString(),
        hoursWorked: parseFloat(formData.hoursWorked),
      };

      if (editing) {
        await api.put(`/time-entries/${editing.id}`, data);
      } else {
        await api.post('/time-entries', data);
      }

      fetchData();
      handleClose();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to save time entry');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this time entry?')) {
      return;
    }

    try {
      await api.delete(`/time-entries/${id}`);
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to delete time entry');
    }
  };

  const handleApprove = async (id: string) => {
    try {
      await api.patch(`/time-entries/${id}/approve`);
      setSnackbar({ open: true, message: 'Time entry approved successfully', severity: 'success' });
      fetchData();
      setSelectedEntries(selectedEntries.filter(e => e !== id));
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to approve time entry', severity: 'error' });
    }
  };

  const handleBulkApprove = async () => {
    if (selectedEntries.length === 0) {
      setSnackbar({ open: true, message: 'Please select entries to approve', severity: 'warning' });
      return;
    }

    try {
      await Promise.all(selectedEntries.map(id => api.patch(`/time-entries/${id}/approve`)));
      setSnackbar({ open: true, message: `${selectedEntries.length} time entries approved successfully`, severity: 'success' });
      fetchData();
      setSelectedEntries([]);
    } catch (error: any) {
      setSnackbar({ open: true, message: 'Failed to approve some entries', severity: 'error' });
    }
  };

  const handleReject = async (id: string, reason: string) => {
    try {
      await api.patch(`/time-entries/${id}/reject`, { reason });
      setSnackbar({ open: true, message: 'Time entry rejected', severity: 'info' });
      fetchData();
      setRejectDialogOpen(false);
      setRejectReason('');
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to reject time entry', severity: 'error' });
    }
  };

  const handleReview = (entry: TimeEntry) => {
    setSelectedEntry(entry);
    setReviewDialogOpen(true);
  };

  const handleSelectEntry = (id: string) => {
    setSelectedEntries(prev => 
      prev.includes(id) 
        ? prev.filter(e => e !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    const pendingEntries = timeEntries.filter(e => e.status === 'PENDING' || e.status === 'SUBMITTED').map(e => e.id);
    setSelectedEntries(prev => 
      prev.length === pendingEntries.length 
        ? [] 
        : pendingEntries
    );
  };

  const exportToExcel = async () => {
    try {
      const params: any = {};
      if (filters.contractorId) params.contractorId = filters.contractorId;
      if (filters.projectId) params.projectId = filters.projectId;

      const response = await api.get('/export/time-entries', {
        params,
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `time-entries-${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'warning';
      case 'SUBMITTED':
        return 'warning';
      case 'APPROVED':
        return 'info';
      case 'BILLED':
        return 'primary';
      case 'PAID':
        return 'success';
      case 'REJECTED':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const isAdmin = user?.role === 'ADMIN';
  const isContractor = user?.role === 'CONTRACTOR';

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Time Entries</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
              {isAdmin && selectedEntries.length > 0 && (
            <Button 
              variant="contained" 
              color="success"
              startIcon={<CheckBoxIcon />} 
              onClick={handleBulkApprove}
            >
              Approve Selected ({selectedEntries.length})
            </Button>
          )}
          <Button variant="outlined" onClick={exportToExcel}>
            Export to Excel
          </Button>
          {isAdmin && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
              Add Time Entry
            </Button>
          )}
        </Box>
      </Box>

      {isAdmin && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>SUBMITTED Status:</strong> Time entries with "SUBMITTED" status have been submitted by contractors and are awaiting your review. 
            Click on any entry to view the full description and approve or reject the hours worked.
          </Typography>
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={2} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Contractor</InputLabel>
            <Select
              value={filters.contractorId}
              label="Contractor"
              onChange={(e) => setFilters({ ...filters, contractorId: e.target.value })}
            >
              <MenuItem value="">All</MenuItem>
              {contractors.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Project</InputLabel>
            <Select
              value={filters.projectId}
              label="Project"
              onChange={(e) => setFilters({ ...filters, projectId: e.target.value })}
            >
              <MenuItem value="">All</MenuItem>
              {projects.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              label="Status"
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="SUBMITTED">Submitted</MenuItem>
              <MenuItem value="APPROVED">Approved</MenuItem>
              <MenuItem value="REJECTED">Rejected</MenuItem>
              <MenuItem value="BILLED">Billed</MenuItem>
              <MenuItem value="PAID">Paid</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Contractor</TableCell>
              <TableCell>Project</TableCell>
              <TableCell align="right">Hours</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Invoice</TableCell>
              {isAdmin && (
                  <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedEntries.length > 0 && selectedEntries.length < timeEntries.filter(e => e.status === 'PENDING' || e.status === 'SUBMITTED').length}
                    checked={timeEntries.filter(e => e.status === 'PENDING' || e.status === 'SUBMITTED').length > 0 && selectedEntries.length === timeEntries.filter(e => e.status === 'PENDING' || e.status === 'SUBMITTED').length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
              )}
              {(isAdmin || isContractor) && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {timeEntries.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell>{format(new Date(entry.dateWorked), 'MMM dd, yyyy')}</TableCell>
                <TableCell>{entry.contractor?.name || '-'}</TableCell>
                <TableCell>{entry.project?.name || '-'}</TableCell>
                <TableCell align="right">{entry.hoursWorked.toFixed(2)}</TableCell>
                <TableCell>
                  <Box sx={{ maxWidth: 400 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        cursor: 'pointer',
                      }}
                      title={entry.description}
                      onClick={() => isAdmin && handleReview(entry)}
                    >
                      {entry.description}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={entry.status} color={getStatusColor(entry.status) as any} size="small" />
                </TableCell>
                <TableCell>{entry.invoice?.invoiceNumber || '-'}</TableCell>
                {isAdmin && (
                  <TableCell padding="checkbox">
                    {(entry.status === 'PENDING' || entry.status === 'SUBMITTED') && (
                      <Checkbox
                        checked={selectedEntries.includes(entry.id)}
                        onChange={() => handleSelectEntry(entry.id)}
                      />
                    )}
                  </TableCell>
                )}
                {(isAdmin || isContractor) && (
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {isAdmin && (
                        <>
                          <Tooltip title="Review Details">
                            <IconButton size="small" onClick={() => handleReview(entry)} color="info">
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {(entry.status === 'PENDING' || entry.status === 'SUBMITTED') && (
                            <>
                              <Tooltip title="Approve">
                                <IconButton size="small" onClick={() => handleApprove(entry.id)} color="success">
                                  <CheckCircleIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reject">
                                <IconButton size="small" onClick={() => { setSelectedEntry(entry); setRejectDialogOpen(true); }} color="error">
                                  <ThumbDownIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </>
                      )}
                      {entry.status !== 'BILLED' && entry.status !== 'PAID' && (
                        <>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleOpen(entry)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => handleDelete(entry.id)} color="error">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit Time Entry' : 'Add Time Entry'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {isAdmin && (
              <FormControl fullWidth>
                <InputLabel>Contractor</InputLabel>
                <Select
                  value={formData.contractorId}
                  label="Contractor"
                  onChange={(e) => setFormData({ ...formData, contractorId: e.target.value })}
                  required
                >
                  {contractors.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl fullWidth>
              <InputLabel>Project</InputLabel>
              <Select
                value={formData.projectId}
                label="Project"
                onChange={(e) => setFormData({ ...formData, projectId: e.target.value })}
                required
              >
                {projects.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Date Worked"
                value={formData.dateWorked}
                onChange={(date) => setFormData({ ...formData, dateWorked: date || new Date() })}
                slotProps={{ textField: { fullWidth: true, required: true } }}
              />
            </LocalizationProvider>
            <TextField
              label="Hours Worked"
              type="number"
              value={formData.hoursWorked}
              onChange={(e) => setFormData({ ...formData, hoursWorked: e.target.value })}
              fullWidth
              required
              inputProps={{ step: '0.25', min: '0' }}
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              required
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onClose={() => setReviewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Review Time Entry
          {selectedEntry && (
            <Chip 
              label={selectedEntry.status} 
              color={getStatusColor(selectedEntry.status) as any} 
              size="small" 
              sx={{ ml: 2 }} 
            />
          )}
        </DialogTitle>
        <DialogContent>
          {selectedEntry && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Contractor</Typography>
                <Typography variant="body1">{selectedEntry.contractor?.name || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Project</Typography>
                <Typography variant="body1">{selectedEntry.project?.name || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Date Worked</Typography>
                <Typography variant="body1">{format(new Date(selectedEntry.dateWorked), 'MMMM dd, yyyy')}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Hours Worked</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedEntry.hoursWorked.toFixed(2)} hours</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Description</Typography>
                <Paper sx={{ p: 2, backgroundColor: '#f5f5f5', maxHeight: 300, overflow: 'auto' }}>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedEntry.description}
                  </Typography>
                </Paper>
              </Box>
              {selectedEntry.invoice && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Invoice</Typography>
                  <Typography variant="body1">{selectedEntry.invoice.invoiceNumber}</Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {(selectedEntry?.status === 'PENDING' || selectedEntry?.status === 'SUBMITTED') && (
            <>
              <Button onClick={() => { setRejectDialogOpen(true); setReviewDialogOpen(false); }} color="error">
                Reject
              </Button>
              <Button onClick={() => { handleApprove(selectedEntry.id); setReviewDialogOpen(false); }} variant="contained" color="success">
                Approve Hours
              </Button>
            </>
          )}
          <Button onClick={() => setReviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => { setRejectDialogOpen(false); setRejectReason(''); }}>
        <DialogTitle>Reject Time Entry</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Rejection Reason"
            fullWidth
            multiline
            rows={4}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Please provide a reason for rejecting this time entry..."
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setRejectDialogOpen(false); setRejectReason(''); }}>Cancel</Button>
          <Button 
            onClick={() => selectedEntry && handleReject(selectedEntry.id, rejectReason)} 
            variant="contained" 
            color="error"
            disabled={!rejectReason.trim()}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TimeEntries;

